<template>
  <v-list class="customer-bookings-list">
    <template v-if="loading">
      <v-list-item v-for="i in 4" :key="i">
        <v-list-item-content>
          <v-list-item-title>
            <v-skeleton-loader type="table-cell" class="skeleton-loader-text-medium-height skeleton-loader-text-medium-width"></v-skeleton-loader>
          </v-list-item-title>
          <v-list-item-subtitle class="mt-1">
            <v-skeleton-loader type="table-cell" class="skeleton-loader-text-small-height skeleton-loader-text-large-width"></v-skeleton-loader>
          </v-list-item-subtitle>
        </v-list-item-content>
        <v-list-item-icon class="ml-2 action-items">
          <v-skeleton-loader type="table-cell" class="skeleton-loader-button"></v-skeleton-loader>
        </v-list-item-icon>
      </v-list-item>
    </template>

    <template v-else-if="bookings.length === 0 && (!bookingGroups || bookingGroups.length === 0)">
      <div class="d-flex flex-column full-width">
        <div class="mx-auto mt-6">
          <v-icon large>mdi-ghost-outline</v-icon>
        </div>
        <p class="text-caption-2 text--secondary my-auto mx-auto py-6">
          Här var det tomt
        </p>
      </div>
    </template>

    <template v-else>
      <div
        v-for="section in sections"
        :key="section.key"
      >
        <v-subheader v-if="section.year">{{ section.year }}</v-subheader>

        <template v-for="item in section.items">
          <!-- Booking group -->
          <v-expansion-panels
            v-if="item.type === 'bookingGroup'"
            :key="'group-' + item.data.id"
            flat
            accordion
            class="mb-1"
          >
            <v-expansion-panel>
              <v-expansion-panel-header class="pa-0 pr-4 highlight-on-hover">
                <v-list-item class="d-flex flex-wrap">
                  <v-list-item-content class="cursor-pointer">
                    <v-list-item-title>
                      <div class="d-flex align-center">
                        <v-icon small color="pink" class="mr-2">mdi-calendar-multiple</v-icon>
                        <span class="text-subtitle-1">{{ item.data.title }}</span>
                        <v-chip
                          class="justify-center ml-2 my-auto"
                          x-small
                          :color="item.data.status_color"
                          text-color="white"
                        >
                          {{ item.data.status_text }}
                        </v-chip>
                      </div>
                    </v-list-item-title>
                    <v-list-item-subtitle>
                      {{ bookingGroupSubtitle(item.data) }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <div
                  v-for="booking in item.data.bookings"
                  :key="booking.id"
                >
                  <customer-bookings-list-row
                    :booking="booking"
                    @clicked="bookingClicked"
                    @close="close"
                    display-customer
                  ></customer-bookings-list-row>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>

          <!-- Individual booking -->
          <customer-bookings-list-row
            v-else
            :key="item.data.id"
            :booking="item.data"
            @clicked="bookingClicked"
            @close="close"
            :display-customer="displayCustomer"
            :belongs-to-external-group="belongsToExternalGroup(item.data)"
          ></customer-bookings-list-row>
        </template>
      </div>
    </template>
  </v-list>
</template>

<script>
import CustomerBookingsListRow from './BookingsListRow.vue'

export default {
  name: 'CustomerBookingsList',
  components: {
    CustomerBookingsListRow
  },
  props: {
    bookings: Array,
    bookingGroups: Array,
    loading: Boolean,
    displayCustomer: {
      type: Boolean,
      default: false
    },
    groupBy: {
      type: String,
      default: 'year'
    }
  },
  data: () => ({}),
  computed: {
    // IDs of bookings that belong to a group in bookingGroups (this customer's groups)
    groupedBookingIds: function () {
      if (!this.bookingGroups || !this.bookingGroups.length) { return new Set() }
      const ids = new Set()
      this.bookingGroups.forEach(bookingGroup => {
        bookingGroup.bookings.forEach(booking => {
          ids.add(booking.id)
        })
      })
      return ids
    },
    // Bookings not already shown inside a booking group
    filteredBookings: function () {
      return this.bookings.filter(b => !this.groupedBookingIds.has(b.id))
    },
    sections: function () {
      const bookings = this.filteredBookings
      const bookingGroups = this.bookingGroups || []

      if (this.groupBy !== 'year') {
        return [{
          key: 'all',
          year: null,
          items: this.buildSortedItems(bookings, bookingGroups)
        }]
      }

      // Collect years from individual bookings and booking groups
      const bookingYears = bookings.map(b => b.check_in.year())
      const groupYears = bookingGroups.map(g => g.first_checkin_date?.year() ?? 'Saknar incheckningsdatum')
      const uniqueYears = [...new Set([...bookingYears, ...groupYears])].sort((a, b) => b - a)

      return uniqueYears.map(year => ({
        key: year,
        year,
        items: this.buildSortedItems(
          bookings.filter(b => b.check_in.year() === year),
          bookingGroups.filter(g => (g.first_checkin_date?.year() ?? 'Saknar incheckningsdatum') === year)
        )
      }))
    }
  },
  methods: {
    bookingClicked: function (booking) {
      this.$emit('booking-clicked', booking)
    },
    close: function () {
      this.$emit('close')
    },
    buildSortedItems: function (bookings, bookingGroups) {
      const items = []

      bookings.forEach(booking => {
        const isUpcoming = !booking.checked_in_at && !booking.checked_out_at && !booking.deleted_at
        items.push({
          type: 'booking',
          data: booking,
          isUpcoming,
          sortDate: isUpcoming ? booking.check_in : booking.check_out
        })
      })

      bookingGroups.forEach(bookingGroup => {
        const isUpcoming = bookingGroup.bookings.every(b => !b.checked_in_at && !b.checked_out_at)
        items.push({
          type: 'bookingGroup',
          data: bookingGroup,
          isUpcoming,
          sortDate: isUpcoming ? bookingGroup.first_checkin_date : bookingGroup.last_checkout_date
        })
      })

      items.sort((a, b) => {
        if (a.isUpcoming && !b.isUpcoming) { return -1 }
        if (!a.isUpcoming && b.isUpcoming) { return 1 }
        return a.sortDate.isAfter(b.sortDate) ? -1 : 1
      })

      return items
    },
    belongsToExternalGroup: function (booking) {
      return booking.booking_group_id != null && !this.groupedBookingIds.has(booking.id)
    },
    bookingGroupSubtitle: function (bookingGroup) {
      const parts = []
      if (bookingGroup.has_mixed_checkin_dates) {
        parts.push(bookingGroup.checkin_dates.map(d => d.toHumanReadableDate()).join(', '))
      } else {
        parts.push(bookingGroup.first_checkin_date?.toHumanReadableDate() ?? 'Saknar incheckningsdatum')
      }
      parts.push('-')
      if (bookingGroup.has_mixed_checkout_dates) {
        parts.push(bookingGroup.checkout_dates.map(d => d.toHumanReadableDate()).join(', '))
      } else {
        parts.push(bookingGroup.last_checkout_date?.toHumanReadableDate() ?? 'Saknar utcheckningsdatum')
      }
      parts.push(`(${bookingGroup.daysFormatted})`)
      return parts.join(' ')
    }
  }
}
</script>
